import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Chip,
  MenuItem,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { formatAmount, isAutoload } from "../../../utils/hooks/dataHandler";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import { currencyRateConversion } from "../../../utils/hooks/dataHandler";

export const headerStyle = (theme) => ({
  fontWeight: 600,
  fontSize: "0.875rem",
  letterSpacing: "0.5px",
  zIndex: 2,
  borderBottom: `2px solid ${theme.palette.divider}`,
  color: "text.primary",
  bgcolor: "rgb(238, 238, 238)",
  py: 1.25,
});

export const EmptyState = ({
  message,
  description = "No adjustment details available for this period",
}) => {
  const theme = useTheme();

  return (
    <Stack
      spacing={2}
      alignItems='center'
      justifyContent='center'
      sx={{
        py: 8,
        px: 4,
        minHeight: 300,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        borderRadius: 3,
        border: "2px dashed",
        borderColor: alpha(theme.palette.primary.main, 0.2),
        m: 2,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          mb: 2,
        }}
      >
        <TableRowsOutlinedIcon
          sx={{
            fontSize: 32,
            color: alpha(theme.palette.primary.main, 0.6),
          }}
        />
      </Box>
      <Typography
        variant='h6'
        color='text.secondary'
        align='center'
        sx={{
          maxWidth: 450,
          fontWeight: 500,
        }}
      >
        {message}
      </Typography>
      <Typography variant='body2' color='text.disabled' align='center'>
        {description}
      </Typography>
    </Stack>
  );
};

export const AdjustmentChip = ({ type }) => {
  const theme = useTheme();
  const isDeduct = type === "Deduct";

  return (
    <Chip
      label={type}
      size='small'
      sx={{
        bgcolor: isDeduct
          ? alpha(theme.palette.error.main, 0.1)
          : alpha(theme.palette.success.main, 0.1),
        color: isDeduct ? theme.palette.error.main : theme.palette.success.main,
        fontWeight: 600,
        "& .MuiChip-icon": {
          color: "inherit",
        },
      }}
    />
  );
};

export const AmountDisplay = ({
  amount,
  type,
  showDecimal = false,
  currency,
  currencyRate,
}) => {
  const theme = useTheme();
  const isDeduct = type === "Deduct";
  const isAddedBack = type === "Added Back";

  const resolvedAmount = isAddedBack
    ? Math.abs(amount)
    : isDeduct
      ? -Math.abs(amount)
      : amount;

  return (
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        color: isDeduct ? theme.palette.error.main : theme.palette.success.main,
        textAlign: "right",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        lineHeight: 1.3,
      }}
    >
      {currency && currency !== "SGD" ? (
        <>
          <Box component='span'>
            {currency}: {formatAmount(resolvedAmount, !showDecimal)}
          </Box>

          <Box
            component='span'
            sx={{
              fontSize: 12,
              opacity: 0.75,
            }}
          >
            SGD:{" "}
            {formatAmount(
              currencyRateConversion(resolvedAmount, currencyRate, "SGD"),
              !showDecimal,
            )}
          </Box>
        </>
      ) : (
        <Box component='span'>{formatAmount(resolvedAmount, !showDecimal)}</Box>
      )}
    </Typography>
  );
};

const compactCellSx = (theme) => ({
  borderColor: alpha(theme.palette.divider, 0.14),
  py: 1.25,
});

const getRowSx = (theme) => ({
  transition: "background-color 0.2s ease-in-out",
  "& > .MuiTableCell-root": {
    ...compactCellSx(theme),
  },
  "& > .MuiTableCell-root:first-of-type": {
    borderLeft: "3px solid transparent",
  },
  "&:nth-of-type(odd) > .MuiTableCell-root": {
    bgcolor: alpha(theme.palette.grey[500], 0.035),
  },
  "&:hover > .MuiTableCell-root": {
    bgcolor: alpha(theme.palette.primary.main, 0.09),
  },
  "&:hover > .MuiTableCell-root:first-of-type": {
    borderLeftColor: theme.palette.primary.main,
  },
});

const clampOffset = (value) => {
  const numericValue = parseInt(value, 10);
  if (Number.isNaN(numericValue)) return 0;
  return Math.max(-1, Math.min(1, numericValue));
};

const offsetFieldSx = {
  minWidth: 72,
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
  },
  "& .MuiSelect-select": {
    py: 0.75,
    fontSize: "0.875rem",
  },
};

const DetailsTable = ({
  dataId,
  data = [],
  noRowsHint = "No adjustment details available.",
  maxHeight = 500,
  onUpdateReference,
  showDecimal = false,
  canEdit = true,
  currency,
  currencyRate,
  isCrossYA = false,
}) => {
  const theme = useTheme();
  const showOffsetColumn = isCrossYA && canEdit;

  if (!data || data.length === 0) {
    return (
      <EmptyState
        message='No adjustments found for this period.'
        description={noRowsHint}
      />
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        overflow: "hidden",
        borderColor: "divider",
        borderWidth: 1,
        borderStyle: "solid",
      }}
    >
      <Box
        sx={{
          maxHeight,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(theme.palette.primary.main, 0.2)} transparent`,

          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.primary.main, 0.2),
            borderRadius: "4px",
            backgroundClip: "padding-box",
          },

          "&:hover::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.primary.main, 0.4),
          },

          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
        }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerStyle(theme),
                  width: isCrossYA ? (showOffsetColumn ? "31%" : "39%") : "51%",
                }}
              >
                Description
              </TableCell>
              <TableCell
                align='center'
                sx={{ ...headerStyle(theme), width: "10%" }}
              >
                Tax Adjustment
              </TableCell>
              {isCrossYA ? (
                <>
                  <TableCell
                    align='right'
                    sx={{ ...headerStyle(theme), width: "11%" }}
                  >
                    Preceding Amount
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{ ...headerStyle(theme), width: "11%" }}
                  >
                    Current Amount
                  </TableCell>
                  {showOffsetColumn && (
                    <TableCell
                      align='right'
                      sx={{ ...headerStyle(theme), width: "8%" }}
                    >
                      Offset
                    </TableCell>
                  )}
                </>
              ) : (
                <TableCell
                  align='right'
                  sx={{ ...headerStyle(theme), width: "12%" }}
                >
                  Amount
                </TableCell>
              )}
              <TableCell
                align='center'
                sx={{ ...headerStyle(theme), width: "10%" }}
              >
                Schedule No
              </TableCell>
              <TableCell
                align='center'
                sx={{ ...headerStyle(theme), width: "15%" }}
              >
                Reference
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={`adjustment-${index}`} sx={getRowSx(theme)}>
                <TableCell>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    {row.name || "-"}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <AdjustmentChip type={row.taxAdjustment} />
                </TableCell>
                {isCrossYA ? (
                  <>
                    <TableCell align='right'>
                      <AmountDisplay
                        amount={row.preceding_amount || 0}
                        type={row.taxAdjustment}
                        showDecimal={showDecimal}
                        currency={currency}
                        currencyRate={currencyRate}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      <AmountDisplay
                        amount={row.current_amount || 0}
                        type={row.taxAdjustment}
                        showDecimal={showDecimal}
                        currency={currency}
                        currencyRate={currencyRate}
                      />
                    </TableCell>
                    {showOffsetColumn && (
                      <TableCell align='right' sx={{ py: 1 }}>
                        <TextField
                          size='small'
                          select
                          value={clampOffset(row.crossYAOffset)}
                          onChange={(event) =>
                            onUpdateReference?.(
                              dataId,
                              {
                                crossYAOffset: parseInt(event.target.value, 10),
                              },
                              index,
                            )
                          }
                          sx={offsetFieldSx}
                          SelectProps={{
                            MenuProps: { disableScrollLock: true },
                          }}
                        >
                          <MenuItem value={-1}>-1</MenuItem>
                          <MenuItem value={0}>0</MenuItem>
                          <MenuItem value={1}>+1</MenuItem>
                        </TextField>
                      </TableCell>
                    )}
                  </>
                ) : (
                  <TableCell align='right'>
                    <AmountDisplay
                      amount={row.amount || 0}
                      type={row.taxAdjustment}
                      showDecimal={showDecimal}
                      currency={currency}
                      currencyRate={currencyRate}
                    />
                  </TableCell>
                )}
                <TableCell align='center'>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {isAutoload(row?.status) ? row.schedule || "-" : "-"}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  {canEdit ? (
                    <TextFieldForName
                      size='small'
                      value={row.reference || ""}
                      onChange={(newValue) =>
                        onUpdateReference(
                          dataId,
                          { reference: newValue },
                          index,
                        )
                      }
                      fullWidth
                      placeholder='Add note...'
                      // sx={referenceFieldSx(theme)}
                    />
                  ) : (
                    <TextFieldForViewOnly value={row.reference || ""} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DetailsTable;
